import { type FC } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import { ReviewScreenLayout } from "@/pages/postloss/modules/components/ReviewScreenLayout";
import { useReviewRedirect } from "@/pages/postloss/modules/hooks/useReviewRedirect";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import {
  CreditCardForm,
  parseItemToForm,
  validateCreditCardFields,
  creditCardToItemFields,
  type CreditCardFormValues,
} from "./CreditCardForm";

export const CreditCardsReview: FC<FlowCustomComponentProps> = ({
  onComplete,
  goToScreen,
  answers,
  setAnswer,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { debts, isLoading, isFetching, updateItem, isUpdatingItem, deleteItem, itemActions } =
    useEstateItemActions(estateId);

  const items = filterByCategory(debts, "Unsecured Debts");
  const firstName = person?.first_name ?? "their";
  const hasExtracted = items.some((p) => p.extracted_from_document);

  const { itemsEverFound, alreadyRedirected } = useReviewRedirect({
    items,
    isLoading,
    isFetching,
    answers,
    setAnswer,
    goToScreen,
    reviewKey: "extracted_credit_cards",
    addKey: "add_credit_cards",
    fallbackScreen: "add_credit_cards",
    fallbackAnswers: {
      ...answers,
      extracted_credit_cards: "none",
    },
  });

  const isRedirecting = !isLoading && items.length === 0 && !itemsEverFound && !alreadyRedirected;

  const handleSaveEdit = async (editing: {
    id: number;
    form: CreditCardFormValues;
    existingFileName: string | null;
    fileRemoved: boolean;
  }) => {
    if (!estateId) return;

    const validationError = Object.values(validateCreditCardFields(editing.form))[0];
    if (validationError) throw new Error(validationError);

    const fields = creditCardToItemFields(editing.form);
    await updateItem(editing.id, {
      item_name: fields.item_name,
      balance: fields.balance,
    });
  };

  return (
    <ReviewScreenLayout<CreditCardFormValues>
      items={items}
      isLoading={isLoading}
      isFetching={isFetching}
      isRedirecting={isRedirecting}
      screenTitle={
        hasExtracted
          ? `We uncovered credit cards in ${firstName}'s documents`
          : `Review ${firstName}'s credit cards`
      }
      screenSubtitle="Review the balances and add any cards we might have missed."
      itemLabel="credit card"
      listTitle="Credit Cards"
      addButtonLabel="Add credit card"
      onAddAnother={() =>
        goToScreen("add_credit_cards", {
          ...answers,
          credit_cards_review: "found",
          add_credit_cards: "adding",
        })
      }
      onContinue={() => onComplete()}
      editDialogTitle="Edit credit card"
      parseItemToForm={parseItemToForm}
      renderEditForm={({ form, onChange }) => <CreditCardForm form={form} onChange={onChange} />}
      onSaveEdit={handleSaveEdit}
      isUpdatingItem={isUpdatingItem}
      deleteItem={deleteItem}
      deleteDialogTitle="Delete Credit Card"
      itemActions={itemActions}
    />
  );
};
